'use client';

import React from 'react';
import { PenLine } from 'lucide-react';

import { SidebarAd } from '@/tracking/ads/components/SidebarAd';
import { useOTSTranslation } from '@/i18n/useOTSTranslation';
import { RouteAndAnalyticsProps } from '@/page-components/pathTypes';
import { IResortLayout } from '@/page-components/resort/_layout';
import {
  IResortPageInfo, IResortPathInfo, IResortReviews, IReviewsGeneral,
} from '@/api/resort/types';
import { Main } from '@/components/resort/Layout/Main';
import { Side } from '@/components/resort/Layout/Side';
import { ReviewsList } from '@/components/resort/ReviewsList';
import { GeneralReviews } from '@/components/resort/GeneralReviews';
import { ReviewFormDynamic } from '@/components/resort/ReviewForm';
import { CtaButton } from '@/components/common/CtaButton';
import { PageBody } from '@/components/common/PageBody';
import { SeoSection } from '@/components/common/SeoSection';
import { Hero400Error } from '@/components/400';
import { Heading } from '@/components/ui/heading';
import { REVIEW_ANCHOR } from './constants';

export type ResortReviewsPageProps = {
  resort: IResortPageInfo,
  pathInfo: IResortPathInfo,
  resortReviews: IResortReviews | null,
  reviewsGeneral: IReviewsGeneral | null,

} & RouteAndAnalyticsProps & IResortLayout;

export const DEBUG_TAG = 'resort-reviews-page';
export const TEMPLATE_ID = '3.2.2';
export const TEMPLATE_NAME = 'Resort Reviews';

const writeReviewCalloutClass = [
  'flex flex-wrap items-center justify-between gap-4',
  'rounded-md border border-primary/15',
  'bg-gradient-to-br from-sky-50 via-sky-50/70 to-background',
  'px-5 py-4',
].join(' ');

export default function ResortReviewsPage({
  resort, pathInfo, resortReviews, reviewsGeneral,
}: ResortReviewsPageProps) {
  const { t } = useOTSTranslation('common');

  if (!resort) {
    return <Hero400Error light />;
  }

  return (
    <>
      <Main>
        <PageBody>
          <SeoSection title={t('resort.reviews.seoTitle', { resort: resort.title })}>
            {t('resort.reviews.seoText', { resort: resort.title })}
          </SeoSection>
          {reviewsGeneral && (
            <GeneralReviews reviewsGeneral={reviewsGeneral} />
          )}
          <section className={writeReviewCalloutClass}>
            <div className="flex min-w-0 items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary text-primary-foreground shadow-sm">
                <PenLine size={20} aria-hidden />
              </div>
              <div className="min-w-0">
                <Heading level={3} className="font-heading text-base font-bold text-secondary">
                  {t('resort.reviews.rateThisResortTitle')}
                </Heading>
                <p className="m-0 text-sm leading-relaxed text-muted-foreground">
                  {t('resort.reviews.rateThisResortText', { resort: resort.title })}
                </p>
              </div>
            </div>
            <CtaButton href={`#${REVIEW_ANCHOR}`}>
              {t('resort.reviews.rateThisResortTitle')}
            </CtaButton>
          </section>
          {resortReviews && (
            <ReviewsList
              reviewsList={resortReviews}
              uuid={pathInfo.uuid}
            />
          )}
          <ReviewFormDynamic
            resortId={pathInfo.uuid}
            resortTitle={pathInfo.title}
            anchorToReviewForm={REVIEW_ANCHOR}
          />
        </PageBody>
      </Main>
      <Side>
        <SidebarAd id={pathInfo.uuid} />
      </Side>
    </>
  );
}

// fetchProps moved to ./reviews.server.ts so the App Router RSC page can
// import the data fetcher without pulling in this 'use client' bundle.
